/**
 * FAMILY GRID - Fastener Sort Utilities
 * 
 * industrial-catalog style sorting for thread sizes, lengths, and other fastener specs.
 * Handles inch (#, fractions) and metric (M) sizes correctly.
 */

window.FastenerSort = {

  // =============================================
  // THREAD SIZE SORTING
  // =============================================

  /**
   * Standard inch machine screw sizes in order
   * Maps gauge number to decimal equivalent for sorting
   */
  inchGaugeOrder: {
    '#0': 0.060,
    '#1': 0.073,
    '#2': 0.086,
    '#3': 0.099,
    '#4': 0.112,
    '#5': 0.125,
    '#6': 0.138,
    '#8': 0.164,
    '#10': 0.190,
    '#12': 0.216,
    '#14': 0.242,
  },

  /**
   * Parse a thread size string and return a numeric value for sorting
   * Handles: #8, #10, 1/4, 5/16, 1/2, M6, M10, etc.
   */
  parseThreadSize: function(str) {
    if (!str || str === '-') return 9999;
    
    str = String(str).trim().toUpperCase();
    
    // Metric: M6, M10, M12, etc.
    const metricMatch = str.match(/^M(\d+(?:\.\d+)?)/i);
    if (metricMatch) {
      // Add 1000 to keep metric separate from inch, sorted after inch
      return 1000 + parseFloat(metricMatch[1]);
    }
    
    // Inch gauge: #0, #2, #4, #6, #8, #10, #12
    const gaugeMatch = str.match(/^#(\d+)/);
    if (gaugeMatch) {
      const gauge = '#' + gaugeMatch[1];
      if (this.inchGaugeOrder[gauge]) {
        return this.inchGaugeOrder[gauge];
      }
      // Unknown gauge, estimate
      return parseInt(gaugeMatch[1]) * 0.013 + 0.060;
    }
    
    // Fractional inch: 1/4, 5/16, 3/8, 1/2, etc.
    const fracMatch = str.match(/^(\d+)?[-\s]?(\d+)\/(\d+)/);
    if (fracMatch) {
      const whole = parseInt(fracMatch[1] || 0);
      const num = parseInt(fracMatch[2]);
      const denom = parseInt(fracMatch[3]);
      return whole + (num / denom);
    }
    
    // Decimal inch: 0.250, .375, etc.
    const decMatch = str.match(/^\.?(\d*\.?\d+)/);
    if (decMatch) {
      return parseFloat(decMatch[1]);
    }
    
    return 9999; // Unknown, sort to end
  },

  /**
   * Compare two thread sizes
   */
  compareThreadSize: function(a, b) {
    return this.parseThreadSize(a) - this.parseThreadSize(b);
  },

  // =============================================
  // LENGTH SORTING
  // =============================================

  /**
   * Parse a length string and return numeric value (in inches or mm)
   * Handles: 1/4", 1-1/2", 25mm, 2.5", etc.
   */
  parseLength: function(str) {
    if (!str || str === '-') return 9999;
    
    str = String(str).trim().toUpperCase();
    
    // Metric mm: 25mm, 50mm, etc. - convert to inches for unified sorting
    const mmMatch = str.match(/^(\d+(?:\.\d+)?)\s*MM/i);
    if (mmMatch) {
      return parseFloat(mmMatch[1]) / 25.4; // Convert to inches
    }
    
    // Mixed fraction: 1-1/2, 2-1/4, etc.
    const mixedMatch = str.match(/^(\d+)[-\s](\d+)\/(\d+)/);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1]);
      const num = parseInt(mixedMatch[2]);
      const denom = parseInt(mixedMatch[3]);
      return whole + (num / denom);
    }
    
    // Simple fraction: 1/4, 3/8, 1/2, etc.
    const fracMatch = str.match(/^(\d+)\/(\d+)/);
    if (fracMatch) {
      return parseInt(fracMatch[1]) / parseInt(fracMatch[2]);
    }
    
    // Decimal: 2.5, 0.75, etc.
    const decMatch = str.match(/^(\d*\.?\d+)/);
    if (decMatch) {
      return parseFloat(decMatch[1]);
    }
    
    return 9999;
  },

  /**
   * Compare two lengths
   */
  compareLength: function(a, b) {
    return this.parseLength(a) - this.parseLength(b);
  },

  // =============================================
  // MATERIAL SORTING
  // =============================================

  /**
   * Material sort order (most common first)
   */
  materialOrder: [
    'steel',
    'carbon steel',
    'medium carbon steel',
    'alloy steel',
    'alloy',
    'grade 2',
    'grade 5', 
    'grade 8',
    '8.8',
    '10.9',
    '12.9',
    'stainless',
    'stainless steel',
    '18-8',
    '18-8 stainless',
    '304',
    '304 stainless',
    '316',
    '316 stainless',
    'a2',
    'a4',
    'brass',
    'bronze',
    'silicon bronze',
    'aluminum',
    'nylon',
    'plastic',
  ],

  /**
   * Parse material for sorting
   */
  parseMaterial: function(str) {
    if (!str || str === '-') return 9999;
    
    const lower = String(str).toLowerCase();
    
    for (let i = 0; i < this.materialOrder.length; i++) {
      if (lower.includes(this.materialOrder[i])) {
        return i;
      }
    }
    
    return 500; // Unknown, sort to middle-end
  },

  /**
   * Compare two materials
   */
  compareMaterial: function(a, b) {
    return this.parseMaterial(a) - this.parseMaterial(b);
  },

  // =============================================
  // GRADE SORTING  
  // =============================================

  gradeOrder: [
    'grade 2', 'gr 2', 'gr2',
    'grade 5', 'gr 5', 'gr5',
    'grade 8', 'gr 8', 'gr8',
    'class 8.8', '8.8',
    'class 10.9', '10.9',
    'class 12.9', '12.9',
    'a2-70', 'a2-80',
    'a4-70', 'a4-80',
    'b7', 'b8',
  ],

  parseGrade: function(str) {
    if (!str || str === '-') return 9999;
    
    const lower = String(str).toLowerCase().replace(/\s+/g, ' ');
    
    for (let i = 0; i < this.gradeOrder.length; i++) {
      if (lower.includes(this.gradeOrder[i])) {
        return i;
      }
    }
    
    return 500;
  },

  compareGrade: function(a, b) {
    return this.parseGrade(a) - this.parseGrade(b);
  },

  // =============================================
  // GENERIC SORT DISPATCHER
  // =============================================

  /**
   * Get the appropriate compare function for a field
   */
  getCompareFunction: function(field) {
    switch (field) {
      case 'diameter':
      case 'thread':
      case 'size':
        return (a, b) => this.compareThreadSize(a, b);
        
      case 'length':
        return (a, b) => this.compareLength(a, b);
        
      case 'material':
        return (a, b) => this.compareMaterial(a, b);
        
      case 'grade':
        return (a, b) => this.compareGrade(a, b);
        
      case 'price':
        return (a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0);
        
      default:
        // String compare for everything else
        return (a, b) => String(a || '').localeCompare(String(b || ''));
    }
  },

  /**
   * Sort an array of products by a field
   */
  sortProducts: function(products, field, direction = 'asc') {
    const compare = this.getCompareFunction(field);
    const mult = direction === 'asc' ? 1 : -1;
    
    return products.sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      return compare(valA, valB) * mult;
    });
  },

  /**
   * Multi-level sort (e.g., by diameter, then length, then material)
   */
  sortProductsMulti: function(products, sortFields) {
    // sortFields = [{field: 'diameter', direction: 'asc'}, {field: 'length', direction: 'asc'}]
    
    return products.sort((a, b) => {
      for (const { field, direction = 'asc' } of sortFields) {
        const compare = this.getCompareFunction(field);
        const mult = direction === 'asc' ? 1 : -1;
        const result = compare(a[field], b[field]) * mult;
        
        if (result !== 0) return result;
      }
      return 0;
    });
  }

};
